import { ensureCustomFilterStyle } from './filterStyles';

/**
 * GradeRangeFilter
 *
 * 급지(0~26) 범위 필터. 슬라이더 + 입력으로 최소~최대 급지 선택.
 * getModel: { min, max } → FilterWrapper 가 "min~max" 문자열로 전달.
 */
const GRADE_MAX = 26;

class GradeRangeFilter {
  constructor() {
    this.model = null; // { min, max }
    this.eGui = null;
    this.params = null;
  }

  init(params) {
    ensureCustomFilterStyle();
    this.params = params;
    this.onClose = params.onClose || null;
    this.eGui = document.createElement('div');
    this.eGui.className = 'aptrank-custom-filter';
    this.render();
  }

  getGui() {
    return this.eGui;
  }

  render() {
    const from = this.model?.min ?? '';
    const to = this.model?.max ?? '';
    const clamp = (n) => Math.max(0, Math.min(GRADE_MAX, Number(n) || 0));
    const sliderMin = from === '' ? 0 : clamp(from);
    const sliderMax = to === '' ? GRADE_MAX : clamp(to);

    this.eGui.innerHTML = `
      <button type="button" class="complex-top-filter-panel-close" style="position: absolute; top: 8px; right: 8px; border: none; background: transparent; color: #64748b; width: 28px; height: 28px; border-radius: 8px; font-size: 24px; line-height: 1; cursor: pointer; z-index: 10;">×</button>
      <div class="aptrank-custom-filter-head">
        <div class="aptrank-custom-filter-heading">급지 범위 (0~${GRADE_MAX})</div>
      </div>
      <div class="aptrank-custom-filter-title-edit">
        <input class="title-min" inputmode="numeric" placeholder="최소" value="${from}" />
        <span>~</span>
        <input class="title-max" inputmode="numeric" placeholder="최대" value="${to}" />
      </div>
      <div class="aptrank-custom-filter-slider-wrap">
        <div class="aptrank-custom-filter-slider-track"></div>
        <input class="aptrank-custom-filter-slider min-slider" type="range" min="0" max="${GRADE_MAX}" step="1" value="${sliderMin}" />
        <input class="aptrank-custom-filter-slider max-slider" type="range" min="0" max="${GRADE_MAX}" step="1" value="${sliderMax}" />
      </div>
      <div class="aptrank-custom-filter-actions">
        <button class="aptrank-custom-filter-reset" type="button">초기화</button>
        <button class="aptrank-custom-filter-apply" type="button">적용</button>
      </div>
    `;

    this.attachEventListeners();
  }

  attachEventListeners() {
    const applyFilter = () => {
      const minRaw = this.eGui.querySelector('input.title-min').value.trim();
      const maxRaw = this.eGui.querySelector('input.title-max').value.trim();
      if (!minRaw && !maxRaw) {
        this.model = null;
      } else {
        const min = minRaw ? Math.max(0, Math.min(GRADE_MAX, Number(minRaw))) : null;
        const max = maxRaw ? Math.max(0, Math.min(GRADE_MAX, Number(maxRaw))) : null;
        this.model = { min, max };
      }
      this.params.filterChangedCallback();
    };

    const updateSlidersFromInputs = () => {
      const minInput = this.eGui.querySelector('input.title-min');
      const maxInput = this.eGui.querySelector('input.title-max');
      const minSlider = this.eGui.querySelector('input.min-slider');
      const maxSlider = this.eGui.querySelector('input.max-slider');
      const minVal = minInput.value.trim();
      const maxVal = maxInput.value.trim();
      if (minVal) minSlider.value = Math.max(0, Math.min(GRADE_MAX, Number(minVal)));
      if (maxVal) maxSlider.value = Math.max(0, Math.min(GRADE_MAX, Number(maxVal)));
    };

    const updateTrack = () => {
      const minSlider = this.eGui.querySelector('input.min-slider');
      const maxSlider = this.eGui.querySelector('input.max-slider');
      const track = this.eGui.querySelector('.aptrank-custom-filter-slider-track');
      if (!track || !minSlider || !maxSlider) return;
      const minVal = Number(minSlider.value);
      const maxVal = Number(maxSlider.value);
      track.style.left = `${(minVal / GRADE_MAX) * 100}%`;
      track.style.right = `${100 - (maxVal / GRADE_MAX) * 100}%`;
    };

    this.eGui.querySelector('input.min-slider').addEventListener('input', (e) => {
      const next = Number(e.target.value);
      const maxText = this.eGui.querySelector('input.title-max').value.trim();
      const max = maxText ? Number(maxText) : GRADE_MAX;
      this.eGui.querySelector('input.title-min').value = String(Math.min(next, max));
      updateTrack();
    });

    this.eGui.querySelector('input.max-slider').addEventListener('input', (e) => {
      const next = Number(e.target.value);
      const minText = this.eGui.querySelector('input.title-min').value.trim();
      const min = minText ? Number(minText) : 0;
      this.eGui.querySelector('input.title-max').value = String(Math.max(next, min));
      updateTrack();
    });

    this.eGui.querySelector('input.title-min').addEventListener('input', () => { updateSlidersFromInputs(); updateTrack(); });
    this.eGui.querySelector('input.title-max').addEventListener('input', () => { updateSlidersFromInputs(); updateTrack(); });

    updateTrack();

    this.eGui.querySelector('.aptrank-custom-filter-apply').addEventListener('click', (e) => {
      e.stopPropagation(); e.preventDefault(); applyFilter();
    });

    this.eGui.querySelector('.aptrank-custom-filter-reset').addEventListener('click', (e) => {
      e.stopPropagation(); e.preventDefault();
      this.eGui.querySelector('input.title-min').value = '';
      this.eGui.querySelector('input.title-max').value = '';
      this.eGui.querySelector('input.min-slider').value = 0;
      this.eGui.querySelector('input.max-slider').value = GRADE_MAX;
      updateTrack();
      this.model = null;
      this.params.filterChangedCallback();
    });

    const closeBtn = this.eGui.querySelector('.complex-top-filter-panel-close');
    if (closeBtn && this.onClose) closeBtn.addEventListener('click', this.onClose);
  }

  getModel() {
    if (!this.model) return null;
    if (this.model.min === null && this.model.max === null) return null;
    return this.model;
  }

  setModel(model) {
    this.model = model || null;
    if (this.eGui) this.render();
  }

  isFilterActive() {
    return this.model !== null && (this.model.min !== null || this.model.max !== null);
  }

  doesFilterPass() { return true; }
}

export default GradeRangeFilter;
